// CRM 2.0 · M12 Template Builder · Map fields & edit (DC-70, DC-71, DC-202): Legal's whole Word file as a page,
// each blank a chip where it is in the text. Click a blank, then click a field; or hold a field and drop it on
// a blank (pointer drag, works on touch). Chips: green field, amber suggestion, red no setting, grey by hand.
// Page fixes (DC-202): select words to remove or replace them, click a paragraph to remove it or its clause
// row, remove one blank; removed = red strike-through, edited = new words (old words on hover); Undo.
// Buyer card (DC-70, DC-71): select the buyer paragraph(s) and mark them as the buyer block; Repeat for each
// applicant (1–4) shows the block repeated with the 1 / 2 / 3 / 4 switch, or Keep as typed (2).
// Pages: hover between two sections shows "+ New page here"; a click draws a dashed "New page starts here" line
// (the section's rule NEW_PAGE), × takes it away (CONTINUE). The system's suggestions are marked "Suggested"; a
// page break in the Word file shows as a line too (removed in Edit in Word). Between sections the hover line is
// used; any other line of the text gets one from the right panel ("Page": a page fix NEW_PAGE). A click in the
// empty space of the page picks the line below it.
// Word comments (not printed) show as a yellow note above their paragraph, tracked changes with a red edge; focus
// (What's pending · Show me) scrolls to a blank, the Needs re-check list, the first comment or tracked change.
// "Buyer details" in one click: the drawer shows the buyer's lines the system found and what prints for buyer 1 and
// 2; Looks right sets them up (suggested buyer fields filled, typed 2nd buyer not printed), Undo takes it back.
// Simple view (owner, 27 Oct 2026): the draft uses the whole width; the tools open in a drawer on the right when a
// space to fill, words or a line is clicked (or "Fields"), and close with ×, Esc or a click outside a space.
// Payment schedule: a table with a mapped payment-plan stage field (sourcePath PlanStage.*) prints the booking's
// own plan; it gets the label "Payment schedule · from system" and its stage row is marked (repeats per stage).
// The label's "Remove the typed rows below" takes away the typed stage rows under it (a Total row stays); "Make
// these words a blank" (selected words) puts a new blank where typed values were, so a field can go there.
// "Make this the applicant list" (a line like "1) MR. ____ )"): one click makes it print once per applicant,
// numbered, with salutation and full name; the typed "2) …" lines below are not printed. Undo takes it all back.
// "Space after each applicant": empty lines printed after every applicant's copy of the block (room for a photo).
// The page HTML comes from the server (escaped there); chips are coloured in place and the scroll position
// is kept after every change.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import cls from "./Templates.module.css";
import { BLANK_KINDS, SUGGESTION_SOURCE, fmtDate, pickTag, pickName, isApplicantField, inApplicantBlock, partyDefaults } from "./templateCommon";
import ApplicantPickModal from "./ApplicantPickModal";
import ScheduleTableModal from "./ScheduleTableModal";
import FieldOrderModal from "./FieldOrderModal";
import { getTemplatePage, addTemplateEdit, undoTemplateEdit, changeTemplateBlocks, getTemplateSections, saveTemplateSections, makeApplicantList, undoApplicantList,
  suggestBuyerDetails, applyBuyerDetails, undoBuyerDetails } from "../../Services/Templates";
import BuyerCard, { BLOCK_NAMES } from "./BuyerCard";
import { errorTost, infoTost, successTost } from "../../Services/Toast";

const GROUPS = [
  ["SELLER", "Seller (Promoter)"], ["PROJECT", "Project"], ["BUYER", "Buyer"], ["UNIT", "Unit"],
  ["PARKING", "Parking"], ["PRICE", "Price"], ["BANK", "Bank"], ["PAYMENT_PLAN", "Payment plan"],
];

// state of one blank on the page: removed, not a blank, ok (field / fixed text), sug (suggestion to confirm), todo, hand
function chipState(b) {
  if (b.removed) return "removed";
  if (b.notBlank) return "notblank";
  if (b.setting === "LEAVE_BLANK") return "hand";
  if (b.setting) return "ok";
  if (b.suggestedFieldKey || b.suggestionSource === "PEN") return "sug";
  return "todo";
}

function chipTag(b, fieldsByKey) {
  if (b.removed) return "";
  if (b.setting === "DATA") return fieldsByKey[b.fieldKey]?.label || b.fieldKey;
  if (b.setting === "FIXED_TEXT") return `“${b.fixedText}”`;
  if (b.setting === "LEAVE_BLANK") return "by hand";
  if (b.suggestedFieldKey) return `${fieldsByKey[b.suggestedFieldKey]?.label || b.suggestedFieldKey}?`;
  if (b.suggestionSource === "PEN") return "by hand?";
  return "";
}

// the sections of the file (the New page page fixes also come as sections, for the QR step; here they are fixes)
const ownSections = (v) => v.sections.filter((s) => !s.fromFix);

// the applicant list still has its fields: each picked field on its space (older lists: the name)
const listNameOk = (list, byNo) => (list.mapped && list.mapped.length
  ? list.mapped.every((x) => byNo[x.blankNo]?.fieldKey === x.fieldKey)
  : byNo[list.nameBlankNo]?.fieldKey === "buyer.fullName");

export function fixTitle(e) {
  if (e.tool === "EDIT_TEXT") return "Words edited";
  if (e.tool === "NOT_A_BLANK") return `Blank ${e.blankNo}: not a blank (kept as typed)`;
  // a re-check fix lost its place: its old number now belongs to another blank, so it is not shown
  if (e.tool === "ADD_BLANK") return e.recheck ? "Added blank (its place changed)" : `Blank ${e.blankNo} added`;
  if (e.tool === "TAB_STOP") return `Lined up after the blank at ${e.posCm} cm`;
  if (e.tool === "NEW_PAGE") return "New page starts here";
  return { WORDS: "Words removed", PARAGRAPH: "Paragraph removed", ROW: "Clause row removed", BLANK: `Blank ${e.blankNo} removed` }[e.target] || "Page fix";
}

// DC-205: where in the paragraph text a click landed (for "Add a blank here"); an empty paragraph = 0
function clickOffset(e, p) {
  if (!p.querySelector("[data-o]")) return 0;
  const r = document.caretRangeFromPoint ? document.caretRangeFromPoint(e.clientX, e.clientY) : null;
  if (!r || !p.contains(r.startContainer)) return null;
  const node = r.startContainer.nodeType === 3 ? r.startContainer.parentElement : r.startContainer;
  const o = node.closest("[data-o]");
  if (!o || !p.contains(o)) return null;
  return Number(o.getAttribute("data-o")) + (r.startContainer.nodeType === 3 ? r.startOffset : 0);
}

// the end of a paragraph's text (a click beside the text, in the cell)
function endOffset(p) {
  const all = [...p.querySelectorAll("[data-o]")];
  const last = all[all.length - 1];
  if (!last) return 0;
  return Number(last.getAttribute("data-o")) + (last.classList.contains("wp-tab") ? 1 : last.textContent.length);
}

// where the reader is on the page: the scroll, and the first paragraph at the top with its distance from the top
function placeOf(sheet) {
  if (!sheet) return null;
  const top = sheet.getBoundingClientRect().top;
  const p = [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getBoundingClientRect().bottom > top);
  return { top: sheet.scrollTop, anchor: p ? p.getAttribute("data-anchor") : null, offset: p ? p.getBoundingClientRect().top - top : 0 };
}

// start / end of a selection in the paragraph text: every text piece on the page has data-o (its start),
// a blank chip has data-s / data-e; a blank cut by the selection counts whole
function selectionOffsets(range, p) {
  let start = null;
  let end = null;
  const take = (a, b) => { start = start === null ? a : Math.min(start, a); end = end === null ? b : Math.max(end, b); };
  p.querySelectorAll("[data-o]").forEach((s) => {
    if (!range.intersectsNode(s)) return;
    const o = Number(s.getAttribute("data-o"));
    const len = s.classList.contains("wp-tab") ? 1 : s.textContent.length;
    const t = s.firstChild;
    const a = t && range.startContainer === t ? o + range.startOffset : o;
    const b = t && range.endContainer === t ? o + range.endOffset : o + len;
    if (b > a) take(a, b);
  });
  p.querySelectorAll("[data-blank]").forEach((c) => {
    if (range.intersectsNode(c)) take(Number(c.getAttribute("data-s")), Number(c.getAttribute("data-e")));
  });
  return start !== null && end > start ? { start, end } : null;
}

function MapFieldsPage({ versionId, blanks, fields, settings, canEdit, onSave, onEdit, onFixesChanged, onBlocksChanged, onBlanksChanged, onAllBlanks, saving, setSaving, shownApplicants, setShownApplicants, focus = null }) {
  const sheetRef = useRef(null);
  const [sheetKey, setSheetKey] = useState(0); // a new key puts the server's page back after a failed save
  const keepScroll = useRef(null);
  const [page, setPage] = useState(null);
  const pageRef = useRef(null); // the page now, for a step that runs after it is drawn again
  pageRef.current = page;
  const [selected, setSelected] = useState(null); // { kind: "blank" | "words" | "para" | "fix", … }
  const [q, setQ] = useState("");
  const [fixedText, setFixedText] = useState("");
  const [newWords, setNewWords] = useState("");
  // "Line up after the blank at __ cm" (kept for the next line): starts at the company default (Templates › Settings)
  const defaults = partyDefaults(settings);
  const [tabCm, setTabCm] = useState(String(defaults.buyerLineUpCm || 9));
  const [picking, setPicking] = useState(null); // { blankNo, fieldKey, pick } while "Which applicant?" is open
  const [secs, setSecs] = useState(null); // the sections with their page rule: the New page lines
  const [drawer, setDrawer] = useState(false); // the drawer opened with "Fields" (it also opens for a selection)
  const [buyerPlan, setBuyerPlan] = useState(null); // "Buyer details" in one click: what the system found
  const [tableModal, setTableModal] = useState(null); // { anchor, item }: the payment schedule table's columns pop-up
  const [orderModal, setOrderModal] = useState(null); // "Which fields do you want?": { kind: buyer, near, spaces } | { kind: signature, anchor }
  const drag = useRef(null);
  const recheckRef = useRef(null);

  const allowed = settings?.pageFixAllowed;
  const may = (tool) => canEdit && (!Array.isArray(allowed) || allowed.includes(tool));

  useEffect(() => {
    let alive = true;
    setPage(null);
    getTemplatePage(versionId).then((p) => alive && setPage(p)).catch((e) => errorTost(e.message));
    setSecs(null);
    getTemplateSections(versionId).then((v) => alive && setSecs(ownSections(v))).catch((e) => errorTost(e.message));
    return () => { alive = false; };
  }, [versionId]);

  // a new page HTML (after a fix) keeps the place the reader was at: first at once, then again by the paragraph
  // that was at the top, after the buyer card has put its labels and repeated blocks back (they move the text)
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    if (sheet && keepScroll.current) sheet.scrollTop = keepScroll.current.top;
  }, [page, sheetKey]);
  useEffect(() => {
    const keep = keepScroll.current;
    if (!keep) return undefined;
    const timer = setTimeout(() => {
      keepScroll.current = null;
      const sheet = sheetRef.current;
      const p = sheet && keep.anchor ? [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === keep.anchor) : null;
      if (p) sheet.scrollTop += p.getBoundingClientRect().top - sheet.getBoundingClientRect().top - keep.offset;
    }, 0);
    return () => clearTimeout(timer);
  }, [page, sheetKey]);

  const fieldsByKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, f])), [fields]);
  const byNo = useMemo(() => Object.fromEntries(blanks.map((b) => [b.blankNo, b])), [blanks]);
  const fixes = useMemo(() => page?.edits || [], [page]);
  const fixById = useMemo(() => Object.fromEntries(fixes.map((e) => [e.editId, e])), [fixes]);
  const html = useMemo(() => ({ __html: page ? page.html : "" }), [page]);
  const sel = selected?.kind === "blank" ? byNo[selected.blankNo] : null;
  const selBlankNo = sel ? sel.blankNo : null;

  // colour the chips in place
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !page) return;
    sheet.querySelectorAll("[data-blank]").forEach((el) => {
      const b = byNo[el.getAttribute("data-blank")];
      if (!b) return;
      const state = chipState(b);
      el.className = `wp-blank ${state}${b.kind === "INSERTED" ? " added" : ""}${b.setting && !b.removed && b.setting !== "LEAVE_BLANK" ? " set" : ""}${selBlankNo === b.blankNo ? " on" : ""}`;
      el.title = `Blank ${b.blankNo} · ${BLANK_KINDS[b.kind] || b.kind}${b.removed ? " · removed" : ""}`;
      let tag = el.querySelector(".wp-tag");
      const text = chipTag(b, fieldsByKey);
      const pick = b.setting === "DATA" && !b.removed ? pickTag(b.applicantPick) : "";
      let pickEl = el.querySelector(".wp-pick");
      if (!text) { if (tag) tag.remove(); if (pickEl) pickEl.remove(); return; }
      if (!tag) { tag = document.createElement("span"); tag.className = "wp-tag"; el.appendChild(tag); }
      tag.textContent = text;
      // DC-204: which applicant, as a small tag; click it to change
      if (!pick) { if (pickEl) pickEl.remove(); return; }
      if (!pickEl) { pickEl = document.createElement("span"); pickEl.className = "wp-pick"; el.appendChild(pickEl); }
      pickEl.textContent = pick;
      pickEl.title = `${pickName(b.applicantPick)} · click to change`;
    });
  }, [page, byNo, fieldsByKey, selBlankNo, sheetKey]);

  // the New page lines, put before the place where each section starts (a clause row: a row of its own)
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !page || !secs) return;
    sheet.querySelectorAll(".wp-np, .wp-np-row").forEach((el) => el.remove());
    const byAnchor = new Map([...sheet.querySelectorAll("p[data-anchor]")].map((p) => [p.getAttribute("data-anchor"), p]));
    const used = new Set();
    // lines added from the right panel (page fix NEW_PAGE)
    fixes.filter((e) => e.tool === "NEW_PAGE" && !e.recheck).forEach((e) => {
      const p = byAnchor.get(e.anchor);
      if (!p || used.has(p)) return;
      used.add(p);
      const line = document.createElement("div");
      line.className = "wp-np on";
      line.setAttribute("data-np-fix", e.editId);
      line.title = e.byName ? `Added by ${e.byName}` : "";
      line.innerHTML = `New page starts here${canEdit ? '<button type="button" class="wp-np-x" aria-label="Remove this line" title="Remove this line">×</button>' : ""}`;
      p.before(line);
    });
    secs.filter((s) => s.found && s.startAnchor).forEach((s) => {
      const p = byAnchor.get(s.startAnchor);
      // a table with a title line starts at the title (startAnchor is then that paragraph, outside the table)
      const at = p && (s.kind === "TABLE" ? p.closest("table") || p : s.kind === "CLAUSE_ROW" ? p.closest("tr") : p);
      if (!at || used.has(at)) return;
      used.add(at);
      const on = s.rule === "NEW_PAGE";
      // a Word page break is already the line; the very start of the file needs none
      if (s.wordBreakBefore || (!on && !canEdit) || (at.parentElement === sheet && !at.previousElementSibling)) return;
      const line = document.createElement("div");
      line.className = `wp-np${on ? " on" : ""}${on && s.suggestedRule === "NEW_PAGE" ? " sug" : ""}`;
      line.setAttribute("data-np", s.docTemplateSectionId);
      if (on) {
        line.innerHTML = `New page starts here${s.suggestedRule === "NEW_PAGE" ? "<i>Suggested</i>" : ""}${canEdit ? '<button type="button" class="wp-np-x" aria-label="Remove this line" title="Remove this line">×</button>' : ""}`;
        line.title = s.suggestedRule === "NEW_PAGE" && s.suggestedWhy ? `Suggested: ${s.suggestedWhy}` : "";
      } else {
        line.innerHTML = '<span class="wp-np-add">+ New page here</span>';
      }
      if (s.kind === "CLAUSE_ROW") {
        const tr = document.createElement("tr");
        tr.className = "wp-np-row";
        const td = document.createElement("td");
        td.colSpan = 99;
        td.appendChild(line);
        tr.appendChild(td);
        at.before(tr);
      } else {
        at.before(line);
      }
    });
  }, [page, sheetKey, secs, canEdit, fixes]);

  // Word comments and tracked changes: they stop Send for approval, so Legal must be able to find them
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !page) return;
    sheet.querySelectorAll(".wp-note").forEach((el) => el.remove());
    const byAnchor = new Map([...sheet.querySelectorAll("p[data-anchor]")].map((p) => [p.getAttribute("data-anchor"), p]));
    (page.notes || []).forEach((n) => {
      const p = byAnchor.get(n.anchor);
      if (!p) return;
      const note = document.createElement("div");
      note.className = "wp-note";
      note.textContent = `Word comment · ${n.author || "Someone"}: “${n.text}”`;
      note.title = "Not printed. Delete it in Edit in Word (or in Word, then Replace file).";
      p.before(note);
    });
    (page.tracked || []).forEach((a) => {
      const p = byAnchor.get(a);
      if (!p) return;
      p.classList.add("wp-tracked");
      p.title = "Tracked change: accept or reject it in Edit in Word";
    });
  }, [page, sheetKey]);

  // the payment schedule tables: worked out from the mapping, so they follow every change at once
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !page) return;
    sheet.querySelectorAll(".wp-sched").forEach((el) => el.remove());
    sheet.querySelectorAll(".wp-sched-row").forEach((el) => { el.classList.remove("wp-sched-row"); el.removeAttribute("title"); });
    const stageKey = new Set(fields.filter((f) => /^PlanStage\./.test(f.sourcePath || "")).map((f) => f.key));
    const rowsOf = new Map(); // table -> its rows with a stage field
    const sugOf = new Map(); // table -> its blanks with a suggested stage field still to accept
    const tableOf = (b) => sheet.querySelector(`[data-blank="${b.blankNo}"]`)?.closest("tr");
    blanks.filter((b) => b.setting === "DATA" && !b.removed && stageKey.has(b.fieldKey)).forEach((b) => {
      const tr = tableOf(b);
      const table = tr && tr.closest("table");
      if (table) rowsOf.set(table, new Set([...(rowsOf.get(table) || []), tr]));
    });
    blanks.filter((b) => !b.setting && !b.removed && !b.notBlank && stageKey.has(b.suggestedFieldKey)).forEach((b) => {
      const table = tableOf(b)?.closest("table");
      if (table) sugOf.set(table, (sugOf.get(table) || 0) + 1);
    });
    new Set([...rowsOf.keys(), ...sugOf.keys()]).forEach((table) => {
      const rows = rowsOf.get(table) || new Set();
      const sug = sugOf.get(table) || 0;
      rows.forEach((tr) => { tr.classList.add("wp-sched-row"); tr.title = "Repeats once per stage of the booking's plan"; });
      const label = document.createElement("div");
      label.className = `wp-sched${rows.size > 1 ? " bad" : !rows.size ? " sug" : ""}`;
      const accept = sug ? `accept the ${sug} suggested field${sug > 1 ? "s" : ""} (the “?” boxes)` : "";
      label.textContent = rows.size > 1 ? `Payment schedule · from system · the stage fields are on ${rows.size} rows: map them on one row`
        : !rows.size ? `Payment schedule · from system · ${accept}: click each box and its field, or Accept all suggestions`
          : `Payment schedule · from system${sug ? ` · ${accept}` : ""}`;
      label.title = "Prints the booking's own payment plan: the marked row repeats once per stage, the rows above it repeat as the heading on each page, the rows below it print once. The Total row (added if the table has none) gets the plan's totals under %, amount, GST and total by itself; a total cell you filled stays. Remove the stage fields to print the table as typed.";
      // the typed stage rows below the repeating row would print as well: one click takes them away (not a Total row)
      const below = rows.size === 1 && canEdit ? [...rows][0] : null;
      const typed = below ? [...below.parentElement.children].slice([...below.parentElement.children].indexOf(below) + 1)
        .filter((tr) => tr.tagName === "TR" && !tr.classList.contains("wp-removed") && !tr.querySelector("[data-blank]") && !/total/i.test(tr.textContent)) : [];
      const anchors = typed.map((tr) => tr.querySelector("p[data-anchor]")?.getAttribute("data-anchor")).filter(Boolean);
      if (anchors.length) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "wp-sched-x";
        btn.textContent = `Remove the ${anchors.length} typed row${anchors.length > 1 ? "s" : ""} below`;
        btn.title = "These typed stage rows would print as well as the booking's plan. A row with Total stays. Undo brings each back.";
        btn.setAttribute("data-rows", JSON.stringify(anchors));
        label.appendChild(btn);
      }
      table.before(label);
    });
  }, [page, sheetKey, blanks, fields, canEdit]);

  // the payment schedule table the system makes (no table in the Word file): shown at its line, click to change it
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet || !page) return;
    sheet.querySelectorAll(".wp-paytable").forEach((el) => el.remove());
    const item = card.items.find((i) => i.blockKey === "PAYMENT_TABLE");
    const p = item && [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === item.anchors[0]);
    if (!p) return;
    const sample = (k) => `${fields.find((f) => f.key === k)?.sampleValue || "…"}${k === "plan.percentage" ? "%" : ""}`;
    const box = document.createElement("div");
    box.className = "wp-paytable";
    box.setAttribute("data-paytable", "1");
    box.title = "Click to change the columns";
    const label = document.createElement("div");
    label.className = "wp-sched";
    label.textContent = "Payment schedule · made by the system from the booking's plan · click to change";
    box.appendChild(label);
    const t = document.createElement("table");
    const head = t.insertRow();
    item.columns.forEach((c) => { const th = document.createElement("th"); th.textContent = c.title; head.appendChild(th); });
    [0, 1].forEach(() => { const r = t.insertRow(); item.columns.forEach((c) => { r.insertCell().textContent = sample(c.key); }); });
    const dots = t.insertRow();
    item.columns.forEach(() => { dots.insertCell().textContent = "…"; });
    if (item.totalRow !== false) {
      const tot = t.insertRow();
      const labelAt = item.columns.findIndex((c) => !["plan.percentage", "plan.amount", "plan.gst", "plan.total", "plan.amountWords", "plan.totalWords"].includes(c.key));
      item.columns.forEach((c, i) => { tot.insertCell().textContent = i === labelAt ? "Total" : c.key === "plan.percentage" ? "100%" : ["plan.amount", "plan.gst", "plan.total"].includes(c.key) ? "…" : ""; });
      tot.className = "tot";
    }
    box.appendChild(t);
    p.after(box);
  }, [page, sheetKey, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // What's pending · Show me: once the page is here, to the place
  const focusDone = useRef(null);
  useEffect(() => {
    const sheet = sheetRef.current;
    if (!focus || !page || !sheet || focusDone.current === focus.n) return;
    focusDone.current = focus.n;
    const flash = (el) => {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("wp-flash");
      setTimeout(() => el.classList.remove("wp-flash"), 2400);
    };
    if (focus.blanks) {
      const no = focus.blanks.find((n) => sheet.querySelector(`[data-blank="${n}"]`));
      if (no) selectBlank(no, true);
      else infoTost(`Blank ${focus.blanks[0]} is not on the page: set it in the List view`);
    } else if (focus.buyerJob) {
      setSelected({ kind: "buyer", near: null });
    } else if (focus.signJob) {
      setSelected({ kind: "signs" });
    } else if (focus.anchor) {
      // a job on the top bar: the line it is about (buyer details, signatures)
      const p = [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === focus.anchor);
      if (p) flash(p);
    } else if (focus.fixes) {
      if (recheckRef.current) recheckRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    } else {
      const el = sheet.querySelector(focus.comments ? ".wp-note" : ".wp-tracked");
      if (el) flash(el);
      else infoTost(focus.comments ? "The comment's place was not found: open Edit in Word, Collaboration › Comments" : "Open Edit in Word, Review, to see the tracked changes");
    }
  }, [focus, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // a line added (NEW_PAGE) or taken away (CONTINUE): shown at once, saved in the background
  async function setRule(id, rule) {
    const before = secs;
    setSecs((list) => list.map((s) => (s.docTemplateSectionId === id ? { ...s, rule } : s)));
    setSaving((n) => n + 1);
    try {
      const v = await saveTemplateSections(versionId, [{ docTemplateSectionId: id, rule }]);
      setSecs(ownSections(v));
    } catch (e) {
      setSecs(before);
      errorTost(e.message);
    } finally {
      setSaving((n) => n - 1);
    }
  }

  useEffect(() => { setFixedText(sel?.setting === "FIXED_TEXT" ? sel.fixedText : ""); }, [sel]);
  useEffect(() => { setNewWords(""); }, [selected]);

  // the space in the middle of the page view, flashed: after the drawer has opened (it moves the page), the
  // page view itself on screen first, then the space inside it
  function selectBlank(blankNo, scroll) {
    setSelected({ kind: "blank", blankNo });
    if (!scroll) return;
    setTimeout(() => {
      const sheet = sheetRef.current;
      const el = sheet?.querySelector(`[data-blank="${blankNo}"]`);
      if (!el) return;
      const box = sheet.getBoundingClientRect();
      if (box.top < 0 || box.bottom > window.innerHeight) sheet.scrollIntoView({ block: "nearest" });
      const at = el.getBoundingClientRect();
      sheet.scrollTop += at.top - sheet.getBoundingClientRect().top - sheet.clientHeight / 2 + at.height / 2;
      el.classList.add("wp-flash");
      setTimeout(() => el.classList.remove("wp-flash"), 1800);
    }, 220);
  }

  // in the order they stand on the page (an added space has a high number but sits where it was added)
  function nextBlank(test, back = false) {
    const onPage = [...(sheetRef.current?.querySelectorAll("[data-blank]:not(.wp-clone [data-blank])") || [])].map((el) => Number(el.getAttribute("data-blank")));
    const order = [...new Set(onPage)].filter((n) => byNo[n]);
    if (back) order.reverse();
    const from = selBlankNo ? order.indexOf(selBlankNo) : -1;
    const hit = [...order.slice(from + 1), ...order.slice(0, from + 1)].find((n) => n !== selBlankNo && test(byNo[n]));
    if (hit) selectBlank(hit, true);
    else infoTost("Every space is filled ✓");
  }
  const toFill = (b) => !b.setting && !b.removed && !b.notBlank;
  const nextRef = useRef(null);
  nextRef.current = () => nextBlank(toFill);
  // after choosing for a space: on to the next space to fill (this browser remembers the choice)
  const [autoNext, setAutoNext] = useState(() => { try { return window.localStorage.getItem("crm2.templates.autoNext") !== "0"; } catch (e) { return true; } });
  const changeAutoNext = (on) => { setAutoNext(on); try { window.localStorage.setItem("crm2.templates.autoNext", on ? "1" : "0"); } catch (e) { /* not kept */ } };

  // the blank turns green at once and stays selected, so Legal can check it; "Next blank without setting →"
  // moves on. The save runs in the background.
  function save(change) {
    const done = onSave(change);
    if (autoNext && change.setting) setTimeout(() => nextRef.current && nextRef.current(), 350);
    return done;
  }

  // an applicant / POA field outside the buyer block first asks "Which applicant?" (DC-204); inside it repeats
  function setField(blankNo, fieldKey) {
    const b = byNo[blankNo];
    if (b && isApplicantField(fieldsByKey[fieldKey]) && !inApplicantBlock(page?.blocks, b.blockKey)) {
      setPicking({ blankNo, fieldKey, pick: b.fieldKey === fieldKey ? b.applicantPick : null });
      return;
    }
    save({ blankNo, setting: "DATA", fieldKey });
  }

  // The fix shows on the page at once (faded until saved); the server's page then replaces it.
  function showNow(body, undoId) {
    const sheet = sheetRef.current;
    if (!sheet) return;
    if (undoId) {
      sheet.querySelectorAll(`[data-edit="${undoId}"]`).forEach((el) => el.classList.add("wp-pending", "wp-undoing"));
      return;
    }
    if (body.target === "WORDS" && selected?.range) {
      try {
        const r = selected.range;
        const del = document.createElement("del");
        del.className = "wp-del wp-pending";
        del.appendChild(r.extractContents());
        r.insertNode(del);
        if (body.tool === "EDIT_TEXT") {
          const ins = document.createElement("ins");
          ins.className = "wp-ins wp-pending";
          ins.textContent = body.newText;
          del.after(ins);
          del.style.display = "none";
        }
      } catch (e) { /* the server's page shows it in a moment */ }
    } else if (body.target === "PARAGRAPH" || body.target === "ROW") {
      const p = [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === body.anchor);
      const el = body.target === "ROW" ? p && p.closest("tr") : p;
      if (el) el.classList.add("wp-removed", "wp-pending");
    } else if (body.target === "BLANK") {
      const chip = sheet.querySelector(`[data-blank="${body.blankNo}"]`);
      if (chip) chip.classList.add("removed", "wp-pending");
    }
  }

  async function changeFixes(fn, body, undoId, okMessage) {
    showNow(body, undoId);
    setSelected(null);
    window.getSelection()?.removeAllRanges();
    setSaving((n) => n + 1);
    try {
      const p = await fn();
      keepScroll.current = placeOf(sheetRef.current);
      setPage(p);
      onFixesChanged(p);
      // DC-205: an added blank joins the list (and is selected); an undone one leaves it
      if (p.addedBlank || p.deletedBlanks) onBlanksChanged(p.addedBlank ? [p.addedBlank] : [], p.deletedBlanks || []);
      if (p.addedBlank) setSelected({ kind: "blank", blankNo: p.addedBlank.blankNo });
      if (okMessage) successTost(okMessage);
    } catch (e) {
      keepScroll.current = placeOf(sheetRef.current);
      setSheetKey((k) => k + 1);
      errorTost(e.message);
    } finally {
      setSaving((n) => n - 1);
    }
  }

  const addFix = (body) => changeFixes(() => addTemplateEdit(versionId, body), body, null, null);

  // Buyer card changes come back as a new page (the block paragraphs marked) and the blanks whose block changed
  // the drawer: open while something is selected or "Fields" was clicked; × / Esc closes it
  const drawerOpen = !!selected || drawer;
  function closeDrawer() {
    setSelected(null);
    setDrawer(false);
    window.getSelection()?.removeAllRanges();
  }
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !/^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "")) closeDrawer(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // the applicant list (and its Undo): the server does every step; the page and all blanks come back
  async function applicantList(fn, okMessage) {
    setSaving((n) => n + 1);
    try {
      const p = await fn();
      keepScroll.current = placeOf(sheetRef.current);
      setPage(p);
      setSelected(null);
      if (onAllBlanks && p.blanks) onAllBlanks(p.blanks, p.blocks);
      else if (p.blocks) onBlocksChanged(p.changedBlanks || [], p.blocks);
      successTost(okMessage);
      if (p.leftOver && p.leftOver.length) infoTost(`Blank ${p.leftOver.join(", ")} (your earlier Signature lines) now print applicant 1 only: remove them if they are not needed`);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setSaving((n) => n - 1);
    }
  }

  // the empty lines after each applicant's copy of the buyer block / signature lines (print once per applicant)
  function gapBox(at) {
    if (card.buyerMode !== "REPEAT") return null;
    const item = card.items.find((i) => (i.blockKey === "SIGNATURE" || i.blockKey === "BUYER") && i.anchors.includes(at.anchor));
    if (!item) return null;
    const lines = Number(item.gapLines) || 0;
    return (
      <div style={{ margin: ".4rem 0 1.2rem" }}>
        <label className={cls.lbl} htmlFor="gapLines">Space for each applicant (e.g. for a photo)</label>
        <select id="gapLines" className={cls.sel} value={lines} disabled={!canEdit}
          onChange={(e) => changeBlocks({ action: "GAP", blockKey: item.blockKey, lines: Number(e.target.value) }, Number(e.target.value) ? `${e.target.value} empty line(s) for each applicant` : "No space for each applicant")}>
          {Array.from({ length: 13 }, (_, n) => <option key={n} value={n}>{n ? `${n} empty line${n > 1 ? "s" : ""}` : "None"}</option>)}
        </select>
        <p className={cls.hint} style={{ marginTop: ".4rem" }}>Printed {item.gapWhere === "BELOW" ? "below" : "above"} every applicant's name (change it in the Buyer card box above). A passport photo (about 4.5 cm) needs about 8 lines.</p>
      </div>
    );
  }

  // the buyer panel: what is found for the clicked line (or the first buyer lines of the agreement)
  useEffect(() => {
    if (selected?.kind !== "buyer") return undefined;
    let alive = true;
    setBuyerPlan(null);
    suggestBuyerDetails(versionId, selected.near || undefined).then((r) => alive && setBuyerPlan(r)).catch((e) => { if (alive) { errorTost(e.message); setBuyerPlan({ found: false }); } });
    return () => { alive = false; };
  }, [selected?.kind, selected?.near, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // a line with a buyer field (set or suggested) that is not already the buyer details
  function hasBuyerField(anchor) {
    const buyerKeys = new Set(fields.filter((f) => f.repeatKind === "EACH_APPLICANT").map((f) => f.key));
    const inBuyer = card.items.some((i) => i.blockKey === "BUYER" && i.anchors.includes(anchor));
    return !inBuyer && blanks.some((b) => b.paragraphAnchor === anchor && !b.removed && buyerKeys.has(b.setting === "DATA" ? b.fieldKey : b.suggestedFieldKey));
  }

  function showLine(anchor) {
    const p = sheetRef.current && [...sheetRef.current.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === anchor);
    if (!p) return;
    p.scrollIntoView({ block: "center", behavior: "smooth" });
    p.classList.add("wp-flash");
    setTimeout(() => p.classList.remove("wp-flash"), 2400);
  }

  function buyerBox() {
    const plan = buyerPlan;
    const auto = card.items.find((i) => i.blockKey === "BUYER" && i.auto);
    if (!plan) return <p className={cls.hint}>Looking for the buyer's lines…</p>;
    if (!plan.found) {
      return (
        <>
          <p className={cls.hint} style={{ marginTop: 0 }}>No lines with buyer fields (name, age, PAN, address…) were found{selected.near ? " at this line" : ""}.</p>
          <p className={cls.hint}>Fill one buyer space first (e.g. the name), or click the buyer's line and choose “Use as Buyer details”.</p>
        </>
      );
    }
    return (
      <>
        {plan.nowPrint ? <div className={cls.okBox} style={{ margin: "0 0 1rem" }}>✓ Buyer details are set up{plan.chosen ? ` with: ${plan.chosen.map((k) => fields.find((f) => f.key === k)?.label || k).join(", ")}` : ""}.</div>
          : plan.added > 0 ? <div className={cls.sugBox} style={{ margin: "0 0 1rem" }}>This adds {plan.added} line{plan.added > 1 ? "s" : ""} that printed for buyer 1 only before (e.g. the address).</div> : null}
        <p className={cls.lbl}>{plan.nowPrint ? "Prints for each buyer now" : "Prints for each buyer"}</p>
        {(plan.nowPrint || [plan.buyer1, plan.buyer2]).map((lines, n) => (
          <div key={n} className={cls.buyerPrint}>
            <b>Buyer {n + 1}</b>
            {lines.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        ))}
        <p className={cls.hint}>
          {plan.anchors.filter(Boolean).length} line{plan.anchors.length > 1 ? "s" : ""}
          {plan.fill.length ? ` · ${plan.fill.length} space${plan.fill.length > 1 ? "s" : ""} filled from suggestions` : ""}
          {plan.copyAnchors.length ? " · the typed 2nd buyer below is not printed" : ""}
          {plan.toFill.length ? ` · ${plan.toFill.length} space${plan.toFill.length > 1 ? "s" : ""} still to fill` : ""}
        </p>
        <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", marginTop: ".8rem" }}>
          {canEdit && (
            <button className={`${cls.btn} ${cls.primary}`}
              onClick={() => setOrderModal({ kind: "buyer", near: selected.near || null, spaces: plan.spaces || [], initial: plan.chosen || defaults.buyerFields.filter((k) => fields.some((f) => f.key === k)) })}>Choose the fields…</button>
          )}
          {canEdit && !plan.nowPrint && (
            <button className={`${cls.btn} ${cls.primary}`} onClick={() => setUpBuyerDefaults()}>Set up with the defaults</button>
          )}
          {canEdit && !plan.same && !plan.nowPrint && (
            <button className={cls.btn} onClick={() => applicantList(() => applyBuyerDetails(versionId, selected.near || undefined), "Buyer details set up: they print once for each buyer")}>Looks right as it is</button>
          )}
          <button className={cls.btn} onClick={() => showLine(plan.anchors[0])}>Show me</button>
          {canEdit && auto && <button className={cls.btn} onClick={() => applicantList(() => undoBuyerDetails(versionId), "Buyer details undone")}>Undo</button>}
        </div>
        {(() => {
          // line up the columns (Age:, PAN No:) of every buyer line with a Tab / spaces after a field: one place for all
          const item = card.items.find((i) => i.blockKey === "BUYER");
          const lined = item && sheetRef.current ? item.anchors.filter((a) => {
            const p = [...sheetRef.current.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === a);
            return p && (p.hasAttribute("data-align") || p.hasAttribute("data-lined"));
          }) : [];
          return lined.length > 0 && (
            <>
              <p className={cls.lbl} style={{ marginTop: "1.4rem" }}>Line up the columns</p>
              <p className={cls.hint} style={{ marginTop: 0 }}>So “Age:” and “PAN No:” start at the same place for every buyer, however long the name is. About 9 cm suits a full name with a salutation.</p>
              {lineUpBox(lined)}
            </>
          );
        })()}
        <p className={cls.hint} style={{ marginTop: "1rem" }}>Not the right lines? Click the buyer's line on the page, then “Use as Buyer details”.</p>
      </>
    );
  }

  // the Signatures panel (top bar chip): what is set up now, or the lines that look like the applicants' line
  // ("1) MR. ____", "Mrs. ____", or a line with the buyer's name / salutation outside the buyer details)
  function signCandidates() {
    if (!sheetRef.current) return [];
    const inBuyer = new Set(card.items.filter((i) => i.blockKey === "BUYER" || i.blockKey === "BUYER_COPY").flatMap((i) => i.anchors));
    const live = blanks.filter((b) => !b.removed);
    const nameLines = new Set(live.filter((b) => ["buyer.fullName", "buyer.salutation"].includes(b.setting === "DATA" ? b.fieldKey : b.suggestedFieldKey)).map((b) => b.paragraphAnchor));
    const withSpace = new Set(live.map((b) => b.paragraphAnchor));
    // a line with the buyer's name field first; else a short "1) MR. ____" line with a space for the name
    return [...sheetRef.current.querySelectorAll("p[data-anchor]")].map((p) => ({ anchor: p.getAttribute("data-anchor"), text: p.textContent.replace(/\s+/g, " ").trim() }))
      .filter((x) => x.text && !inBuyer.has(x.anchor) && (nameLines.has(x.anchor)
        || (withSpace.has(x.anchor) && x.text.length <= 90 && (/^\s*1\s*\)/.test(x.text) || /^\s*(MRS|MR|MS|MISS|SHRI|SMT|KUM|DR)\.?\s/i.test(x.text)))))
      .sort((a, b) => Number(nameLines.has(b.anchor)) - Number(nameLines.has(a.anchor)))
      .slice(0, 3);
  }
  // each buyer's number in front of the signature line: on unless switched off; 1. or 1) (blocks.js NUMBER)
  function numberBox(sig) {
    const typed = /^\s*1\s*[).]/.test(lineText(sig.anchors[0]));
    const on = typeof sig.numbered === "boolean" ? sig.numbered : defaults.signNumber !== "NONE";
    const style = sig.numberStyle || (sig.numbered === true ? "BRACKET" : defaults.signNumber === "BRACKET" ? "BRACKET" : "DOT");
    if (typed) return <p className={cls.hint}>Numbered as typed on the line (“1)”): 1, 2, 3… for each buyer.</p>;
    return (
      <div style={{ display: "flex", gap: ".8rem", alignItems: "center", flexWrap: "wrap", margin: ".8rem 0" }}>
        <label className={cls.chk}>
          <input type="checkbox" checked={on} disabled={!canEdit}
            onChange={(e) => changeBlocks({ action: "NUMBER", blockKey: "SIGNATURE", on: e.target.checked, style }, e.target.checked ? "Each buyer is numbered" : "No numbers in front of the buyers")} /> Number each buyer
        </label>
        <select className={cls.sel} value={style} disabled={!canEdit || !on} aria-label="Number style"
          onChange={(e) => changeBlocks({ action: "NUMBER", blockKey: "SIGNATURE", on: true, style: e.target.value }, "Number style changed")}>
          <option value="DOT">1. 2. 3.</option>
          <option value="BRACKET">1) 2) 3)</option>
        </select>
      </div>
    );
  }
  function lineText(a) {
    const p = sheetRef.current && [...sheetRef.current.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === a);
    return p ? p.textContent.replace(/\s+/g, " ").trim() : "";
  }
  function signsBox() {
    const sig = card.items.find((i) => i.blockKey === "SIGNATURE");
    const labelOf = (k) => fields.find((f) => f.key === k)?.label || k;
    const choose = (anchor, initial) => setOrderModal({ kind: "signature", anchor, initial });
    const showBtn = (a) => <button className={cls.btn} onClick={() => showLine(a)}>Show me</button>;
    if (sig && sig.list) {
      const st = listState();
      return (
        <>
          <div className={cls.okBox} style={{ margin: "0 0 1rem" }}>✓ The applicant list is set up: one numbered line per buyer.</div>
          <p className={cls.lbl}>The line</p>
          <div className={cls.buyerPrint}>{lineText(sig.anchors[0]) || "—"}</div>
          <p className={cls.lbl}>Fields, in order</p>
          <p className={cls.hint} style={{ marginTop: 0 }}>{(sig.list.fields || ["buyer.salutation", "buyer.fullName"]).map(labelOf).join(", ")}</p>
          {numberBox(sig)}
          {st && !st.ok && repairBox(st, { margin: ".8rem 0" })}
          <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", marginTop: ".8rem" }}>
            {canEdit && <button className={`${cls.btn} ${cls.primary}`} onClick={() => choose(sig.anchors[0], sig.list.fields || ["buyer.salutation", "buyer.fullName"])}>Choose the fields…</button>}
            {showBtn(sig.anchors[0])}
            {canEdit && <button className={cls.btn} onClick={() => applicantList(() => undoApplicantList(versionId), "Applicant list undone")}>Undo</button>}
          </div>
        </>
      );
    }
    if (sig) {
      // the lines: the ones printed with buyer 1 only (leadLines, right above) and the ones repeated for each buyer
      const all = sheetRef.current ? [...sheetRef.current.querySelectorAll("p[data-anchor]")].map((p) => p.getAttribute("data-anchor")) : [];
      const at = all.indexOf(sig.anchors[0]);
      const lead = sig.leadLines && at > 0 ? all.slice(Math.max(0, at - sig.leadLines), at) : [];
      const rows = [...lead.map((a) => ({ a, each: false })), ...sig.anchors.map((a) => ({ a, each: true }))].filter((r) => lineText(r.a));
      const named = blanks.some((b) => !b.removed && sig.anchors.includes(b.paragraphAnchor) && isApplicantField(fields.find((f) => f.key === (b.setting === "DATA" ? b.fieldKey : null))));
      return (
        <>
          {named ? <div className={cls.okBox} style={{ margin: "0 0 1rem" }}>✓ Signatures are set up.</div>
            : <div className={cls.sugBox} style={{ margin: "0 0 1rem" }}>These lines have no buyer field (e.g. the full name) to repeat: fill one, or choose “Each buyer” only for the name line.</div>}
          <p className={cls.lbl}>Each line prints</p>
          {rows.map((r) => (
            <div key={r.a} style={{ marginBottom: ".8rem" }}>
              <div className={cls.buyerPrint} style={{ marginBottom: ".4rem" }}>{lineText(r.a)}</div>
              <select className={cls.sel} value={r.each ? "EACH" : "FIRST"} disabled={!canEdit} aria-label="How this line prints"
                onChange={(e) => setSignLines(rows.map((x) => (x.a === r.a ? { ...x, each: e.target.value === "EACH" } : x)), sig)}>
                <option value="EACH">For each buyer</option>
                <option value="FIRST">Once, with buyer 1</option>
              </select>
            </div>
          ))}
          <p className={cls.hint}>“Once, with buyer 1” lines stay on top (e.g. SIGNED AND DELIVERED); the lines below repeat for buyer 1, 2, 3…</p>
          {numberBox(sig)}
          <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap", marginTop: ".8rem" }}>
            {showBtn(rows[0] ? rows[0].a : sig.anchors[0])}
            {canEdit && <button className={cls.btn} onClick={() => changeBlocks({ action: "CLEAR", blockKey: "SIGNATURE" }, "Signatures cleared")}>Clear</button>}
          </div>
        </>
      );
    }
    const found = signCandidates();
    return (
      <>
        <p className={cls.lbl}>{found.length ? "Lines that look like the buyers' line" : "The buyers' line"}</p>
        {!found.length && <p className={cls.hint} style={{ marginTop: 0 }}>None found. Click the line where the first buyer signs (e.g. “1) MR. ____”), then “Make this the applicant list”. A line with no space for the name: click on it and use “＋ Add a blank here” first.</p>}
        {found.map((x) => (
          <div key={x.anchor} style={{ marginBottom: "1rem" }}>
            <div className={cls.buyerPrint}>{x.text}</div>
            <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
              {canEdit && <button className={`${cls.btn} ${cls.primary}`} onClick={() => choose(x.anchor, defaults.signFields)}>Use this line…</button>}
              {showBtn(x.anchor)}
            </div>
          </div>
        ))}
        <p className={cls.hint} style={{ marginTop: "1rem" }}>You choose the fields (e.g. Salutation, Full name). The line then prints once per buyer, numbered 1), 2), 3)…; the typed “2) …” lines below it are not printed. Undo takes it all back.</p>
      </>
    );
  }

  // "Set up with the defaults" (Templates › Settings › Buyer & signatures): the default fields, then the columns lined
  // up at the default place on the buyer lines that have a Tab / spaces after a field
  async function setUpBuyerDefaults() {
    const keys = defaults.buyerFields.filter((k) => fields.some((f) => f.key === k));
    await applicantList(() => applyBuyerDetails(versionId, selected?.near || undefined, keys), "Buyer details set up with the defaults");
    if (!(defaults.buyerLineUpCm > 0)) return;
    // the page is drawn again first: then the lines that can be lined up are known
    setTimeout(async () => {
      const item = (pageRef.current?.blocks?.items || []).find((i) => i.blockKey === "BUYER");
      if (!item || !sheetRef.current) return;
      const lined = item.anchors.filter((a) => {
        const p = [...sheetRef.current.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === a);
        return p && (p.hasAttribute("data-align") || p.hasAttribute("data-lined"));
      });
      if (lined.length) await lineUp(lined, defaults.buyerLineUpCm);
    }, 600);
  }

  // the applicant list lost its fields (an Edit in Word save could lose them): the name blank without its field,
  // the salutation blank gone; shown above the page and on the name blank, with Repair
  function listState() {
    const sig = card.items.find((i) => i.blockKey === "SIGNATURE" && i.list);
    if (!sig) return null;
    const byNoNow = Object.fromEntries(blanks.map((b) => [b.blankNo, b]));
    const nameOk = listNameOk(sig.list, byNoNow);
    const salOk = !sig.list.salutationBlankNo || byNoNow[sig.list.salutationBlankNo]?.fieldKey === "buyer.salutation";
    const lost = [!nameOk ? "name" : null, !salOk ? "salutation" : null].filter(Boolean).join(" and ");
    return { sig, ok: nameOk && salOk, lost, repair: () => applicantList(() => makeApplicantList(versionId, sig.anchors[0]), "Applicant list repaired") };
  }
  function repairBox(st, style) {
    return (
      <div className={cls.blockBox} style={style}>
        The applicant list lost its {st.lost} field (the Word file was changed), so the names print as a red blank. Repair makes it again.
        {canEdit && <div style={{ marginTop: ".6rem" }}><button className={`${cls.btn} ${cls.primary}`} onClick={st.repair}>Repair applicant list</button></div>}
      </div>
    );
  }

  function applicantListBox(at) {
    const sig = card.items.find((i) => i.blockKey === "SIGNATURE");
    const isList = sig && sig.list && sig.anchors.includes(at.anchor);
    const looks = /^\s*1\s*[).]/.test(at.text) || /^\s*(MRS|MR|MS|MISS|SHRI|SMT|KUM|DR)\.?\s/i.test(at.text);
    if (isList) {
      const name = fields.find((f) => f.key === "buyer.fullName")?.sampleValue || "Asha Rao";
      // its fields gone (an Edit in Word save can lose them): the name blank without its field, the salutation blank gone
      const byNoNow = Object.fromEntries(blanks.map((b) => [b.blankNo, b]));
      const nameOk = listNameOk(sig.list, byNoNow);
      const salOk = !sig.list.salutationBlankNo || byNoNow[sig.list.salutationBlankNo]?.fieldKey === "buyer.salutation";
      const repair = () => applicantList(() => makeApplicantList(versionId, at.anchor), "Applicant list repaired");
      return (
        <div style={{ margin: ".4rem 0 1.2rem" }}>
          <p className={cls.lbl}>Applicant list</p>
          {nameOk && salOk ? (
            <p className={cls.hint} style={{ marginTop: 0 }}>Prints one line per applicant, numbered: “1) Mr. {name} ) · 2) Mrs. {name} (2) ) …”. The typed lines below are not printed.</p>
          ) : (
            <div className={cls.blockBox} style={{ margin: "0 0 .8rem" }}>
              The list lost its {!nameOk ? "name" : ""}{!nameOk && !salOk ? " and " : ""}{!salOk ? "salutation" : ""} field (the Word file was changed). Click Repair: it is made again.
              {canEdit && <div style={{ marginTop: ".6rem" }}><button className={`${cls.btn} ${cls.primary}`} onClick={repair}>Repair applicant list</button></div>}
            </div>
          )}
          {canEdit && <button className={cls.btn} onClick={() => applicantList(() => undoApplicantList(versionId), "Applicant list undone")}>Undo applicant list</button>}
        </div>
      );
    }
    if (!canEdit || !looks || at.inRow) return null;
    return (
      <div style={{ margin: ".4rem 0 1.2rem" }}>
        <p className={cls.lbl}>Applicants</p>
        <button className={`${cls.btn} ${cls.primary}`} onClick={() => setOrderModal({ kind: "signature", anchor: at.anchor, initial: defaults.signFields })}>＋ Make this the applicant list</button>
        <p className={cls.hint} style={{ marginTop: ".4rem" }}>One click: this line prints once per applicant with 1), 2), 3)…, the salutation (Mr. / Mrs. / Miss.) and the full name; the typed “2) …” lines below are hidden. Undo takes it all back.</p>
      </div>
    );
  }

  async function setSignLines(rows, sig) {
    const first = rows.findIndex((r) => r.each);
    if (first < 0) { errorTost("At least one line prints for each buyer (the name line)"); return; }
    if (rows.slice(first).some((r) => !r.each)) { errorTost("Lines printed once, with buyer 1, go above the lines that repeat"); return; }
    setSaving((n) => n + 1);
    try {
      // the blanks changed by every step (the last one per blank wins)
      const changed = new Map();
      const step = async (change) => { const r = await changeTemplateBlocks(versionId, change); (r.changedBlanks || []).forEach((b) => changed.set(b.blankNo, b)); return r; };
      await step({ action: "CLEAR", blockKey: "SIGNATURE" });
      let p = await step({ action: "SET", blockKey: "SIGNATURE", lead: false, anchors: rows.slice(first).map((r) => r.a),
        ...(typeof sig.numbered === "boolean" ? { numbered: sig.numbered } : {}), ...(sig.numberStyle ? { numberStyle: sig.numberStyle } : {}) });
      if (first > 0) p = await step({ action: "LEAD", blockKey: "SIGNATURE", on: true });
      if (Number(sig.gapLines)) p = await step({ action: "GAP", blockKey: "SIGNATURE", lines: Number(sig.gapLines), where: sig.gapWhere || "ABOVE" });
      if (sig.signLine) p = await step({ action: "SIGNLINE", blockKey: "SIGNATURE", on: true });
      keepScroll.current = placeOf(sheetRef.current);
      setPage(p);
      onBlocksChanged([...changed.values()], p.blocks);
      successTost(first > 0 ? `${first} line(s) print once, with buyer 1; the rest for each buyer` : "Every line prints for each buyer");
    } catch (e) {
      errorTost(e.message);
    } finally {
      setSaving((n) => n - 1);
    }
  }

  async function changeBlocks(change, okMessage) {
    setSaving((n) => n + 1);
    try {
      const p = await changeTemplateBlocks(versionId, change);
      keepScroll.current = placeOf(sheetRef.current);
      setPage(p);
      setSelected(null);
      window.getSelection()?.removeAllRanges();
      onBlocksChanged(p.changedBlanks || [], p.blocks);
      if (okMessage) successTost(okMessage);
    } catch (e) {
      errorTost(e.message);
    } finally {
      setSaving((n) => n - 1);
    }
  }
  const undoFix = (editId) => changeFixes(() => undoTemplateEdit(versionId, editId), null, editId, "Page fix undone");

  // a mouse-up with words selected inside one paragraph = words mode
  function onSheetMouseUp() {
    const s = window.getSelection();
    if (!s || s.isCollapsed || !s.rangeCount || !sheetRef.current) return;
    const range = s.getRangeAt(0);
    if (!sheetRef.current.contains(range.commonAncestorContainer)) return;
    const node = (n) => (n.nodeType === 3 ? n.parentElement : n);
    const p1 = node(range.startContainer).closest("p[data-anchor]");
    const p2 = node(range.endContainer).closest("p[data-anchor]");
    if (!p1 || !p2) return;
    if (p1 !== p2) {
      // several paragraphs: they can become a buyer / signature block
      const all = [...sheetRef.current.querySelectorAll("p[data-anchor]")];
      const from = all.indexOf(p1);
      const to = all.indexOf(p2);
      const list = all.slice(Math.min(from, to), Math.max(from, to) + 1);
      setSelected({ kind: "paras", anchors: list.map((p) => p.getAttribute("data-anchor")), text: list.map((p) => p.textContent.trim()).join(" "),
        align: list.filter((p) => p.hasAttribute("data-align") || p.hasAttribute("data-lined")).map((p) => p.getAttribute("data-anchor")) });
      return;
    }
    const at = selectionOffsets(range, p1);
    if (at) setSelected({ kind: "words", anchor: p1.getAttribute("data-anchor"), ...at, text: s.toString().trim(), range: range.cloneRange() });
  }

  function onSheetClick(e) {
    if (e.target.closest("[data-paytable]")) {
      const item = card.items.find((i) => i.blockKey === "PAYMENT_TABLE");
      if (item) setTableModal({ anchor: item.anchors[0], item });
      return;
    }
    const typedRows = e.target.closest(".wp-sched-x");
    if (typedRows) {
      if (canEdit) {
        const anchors = JSON.parse(typedRows.getAttribute("data-rows") || "[]");
        changeFixes(() => addTemplateEdit(versionId, { batch: anchors.map((anchor) => ({ tool: "REMOVE_TEXT", target: "ROW", anchor })) }), {}, null, `${anchors.length} typed row(s) removed`);
      }
      return;
    }
    const fixLine = e.target.closest("[data-np-fix]");
    if (fixLine) {
      if (canEdit && e.target.closest(".wp-np-x")) undoFix(fixLine.getAttribute("data-np-fix"));
      return;
    }
    const line = e.target.closest("[data-np]");
    if (line) {
      const s = canEdit && secs && secs.find((x) => x.docTemplateSectionId === line.getAttribute("data-np"));
      if (s && e.target.closest(".wp-np-x")) setRule(s.docTemplateSectionId, "CONTINUE");
      else if (s && s.rule !== "NEW_PAGE") setRule(s.docTemplateSectionId, "NEW_PAGE");
      return;
    }
    const s = window.getSelection();
    if (s && !s.isCollapsed) return;
    const pickEl = e.target.closest(".wp-pick");
    if (pickEl && canEdit) {
      const b = byNo[pickEl.closest("[data-blank]").getAttribute("data-blank")];
      if (b) return setPicking({ blankNo: b.blankNo, fieldKey: b.fieldKey, pick: b.applicantPick });
    }
    const chip = e.target.closest("[data-blank]");
    if (chip) return selectBlank(Number(chip.getAttribute("data-blank")), false);
    const fix = e.target.closest("[data-edit]");
    if (fix && fixById[fix.getAttribute("data-edit")]) return setSelected({ kind: "fix", editId: fix.getAttribute("data-edit") });
    let p = e.target.closest("p[data-anchor]");
    let at = p ? clickOffset(e, p) : null;
    // a click in a table cell beside its text: the cell's last paragraph, at its end
    if (!p) {
      const td = e.target.closest("td");
      const ps = td ? [...td.querySelectorAll("p[data-anchor]")].filter((x) => x.closest("td") === td) : [];
      p = ps[ps.length - 1] || null;
      at = p ? endOffset(p) : null;
      // a click in the empty space of the page: the line below it
      if (!td && sheetRef.current) {
        p = [...sheetRef.current.querySelectorAll("p[data-anchor]")].find((x) => !x.closest("td") && x.getBoundingClientRect().bottom > e.clientY) || null;
        at = p ? 0 : null;
      }
    }
    if (p) {
      setSelected({ kind: "para", anchor: p.getAttribute("data-anchor"), inRow: !!p.closest("tr"), text: p.textContent.trim(), at, empty: !p.querySelector("[data-o]"),
        align: p.hasAttribute("data-align") || p.hasAttribute("data-lined") ? [p.getAttribute("data-anchor")] : [] });
    }
  }

  // pointer drag: works with mouse and touch, also where the browser drag-and-drop does not
  function dragStart(e, field) {
    if (!canEdit || (e.button !== undefined && e.button !== 0)) return;
    e.preventDefault();
    const ghost = document.createElement("div");
    ghost.className = cls.dragGhost;
    ghost.textContent = field.label;
    document.body.appendChild(ghost);
    drag.current = { field, ghost, x0: e.clientX, y0: e.clientY, moved: false, over: null };
    const move = (ev) => {
      const d = drag.current;
      if (!d) return;
      d.ghost.style.left = `${ev.clientX + 12}px`;
      d.ghost.style.top = `${ev.clientY + 12}px`;
      if (Math.abs(ev.clientX - d.x0) + Math.abs(ev.clientY - d.y0) > 6) d.moved = true;
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const chip = el && sheetRef.current?.contains(el) ? el.closest("[data-blank]") : null;
      if (d.over && d.over !== chip) d.over.classList.remove("over");
      if (chip) chip.classList.add("over");
      d.over = chip;
    };
    const end = (ev) => {
      window.removeEventListener("pointermove", move);
      const d = drag.current;
      drag.current = null;
      if (!d) return;
      d.ghost.remove();
      if (d.over) d.over.classList.remove("over");
      if (!d.moved) {
        if (selBlankNo) setField(selBlankNo, field.key);
        else infoTost("Click a blank on the page first, or drag the field onto a blank");
        return;
      }
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const chip = el && sheetRef.current?.contains(el) ? el.closest("[data-blank]") : null;
      if (chip) {
        const no = Number(chip.getAttribute("data-blank"));
        setSelected({ kind: "blank", blankNo: no });
        setField(no, field.key);
      } else if (el && sheetRef.current?.contains(el)) {
        infoTost("Drop the field on a blank (a coloured box). Adding a new field inside the text comes later.");
      }
    };
    move(e);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end, { once: true });
  }

  // right panel "Page": a new page starting at the clicked line. Where a section starts, its own line (rule);
  // anywhere else in the text a page fix NEW_PAGE; not inside a table (only at a section's row)
  function newPageBox(at) {
    const sheet = sheetRef.current;
    const elOf = (anchor) => sheet && [...sheet.querySelectorAll("p[data-anchor]")].find((x) => x.getAttribute("data-anchor") === anchor);
    const tr = at.inRow ? elOf(at.anchor)?.closest("tr") : null;
    const sec = (secs || []).find((s) => s.found && s.startAnchor && (s.startAnchor === at.anchor
      || (tr && (s.kind === "CLAUSE_ROW" || s.kind === "TABLE") && elOf(s.startAnchor)?.closest("tr") === tr)));
    const fix = fixes.find((e) => e.tool === "NEW_PAGE" && !e.recheck && e.anchor === at.anchor);
    let body;
    if (sec && sec.wordBreakBefore) body = <p className={cls.hint}>A page break in the Word file starts a new page here.</p>;
    else if (fix || (sec && sec.rule === "NEW_PAGE")) {
      body = (
        <>
          <p className={cls.hint}>A new page starts here.</p>
          {canEdit && <button className={cls.btn} onClick={() => (fix ? undoFix(fix.editId) : setRule(sec.docTemplateSectionId, "CONTINUE"))}>Remove the new page line</button>}
        </>
      );
    } else if (!canEdit) return null;
    else if (sec) body = <button className={cls.btn} onClick={() => setRule(sec.docTemplateSectionId, "NEW_PAGE")}>＋ New page starts here</button>;
    else if (at.inRow) body = <p className={cls.hint}>A new page cannot start inside a table. Click the line above the table.</p>;
    else {
      body = (
        <>
          <button className={cls.btn} onClick={() => addFix({ tool: "NEW_PAGE", target: "BREAK", anchor: at.anchor })}>＋ New page starts here</button>
          <p className={cls.hint} style={{ marginTop: ".4rem" }}>{at.empty ? "At the next line with words below." : "This line starts the new page."}</p>
        </>
      );
    }
    return (
      <div style={{ margin: ".4rem 0 1.2rem" }}>
        <p className={cls.lbl}>Page</p>
        {body}
      </div>
    );
  }

  const query = q.trim().toLowerCase();
  const shownFields = fields.filter((f) => !query || f.label.toLowerCase().includes(query) || f.key.toLowerCase().includes(query));
  const groups = [...GROUPS, ...[...new Set(fields.map((f) => f.fieldGroup))].filter((g) => !GROUPS.some(([k]) => k === g)).map((g) => [g, g])]
    .map(([key, name]) => [name, shownFields.filter((f) => f.fieldGroup === key)])
    .filter(([, list]) => list.length);
  const count = (s) => blanks.filter((b) => chipState(b) === s).length;
  const recheck = fixes.filter((e) => e.recheck);
  const fixOfBlank = sel?.removed ? fixes.find((e) => !e.recheck && (e.removesBlanks || []).includes(sel.blankNo)) : null;
  const addedFix = sel?.kind === "INSERTED" ? fixes.find((e) => e.tool === "ADD_BLANK" && !e.recheck && e.blankNo === sel.blankNo) : null;
  const shownFix = selected?.kind === "fix" ? fixById[selected.editId] : null;
  // one way to take a blank away, for a found blank (a page fix: Undo brings it back) and an added one (it goes)
  const canRemove = !!sel && !sel.removed && (sel.kind === "INSERTED" ? canEdit && !!addedFix : may("REMOVE_TEXT"));
  const removeSel = () => {
    if (!canRemove) return;
    if (sel.kind === "INSERTED") changeFixes(() => undoTemplateEdit(versionId, addedFix.editId), null, addedFix.editId, `Blank ${sel.blankNo} removed`);
    else addFix({ tool: "REMOVE_TEXT", target: "BLANK", blankNo: sel.blankNo });
  };
  const removeRef = useRef(removeSel);
  removeRef.current = removeSel;
  // Delete key on the selected blank (not while typing in a box)
  useEffect(() => {
    if (!canRemove) return undefined;
    const onKey = (e) => {
      if (e.key !== "Delete" || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "")) return;
      e.preventDefault();
      removeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canRemove, selBlankNo]);

  // text after a blank lined up with spaces / Tabs without a tab stop: one tab stop for these lines (TAB_STOP)
  const lineUp = async (anchors, cm = Number(tabCm)) => {
    for (const anchor of anchors) await addFix({ tool: "TAB_STOP", target: "LINE", anchor, posCm: cm });
  };
  const lineUpBox = (anchors) => canEdit && anchors?.length > 0 && (
    <div className={cls.sugBox} style={{ marginTop: "1.2rem" }}>
      <b>{anchors.length > 1 ? `${anchors.length} lines line` : "This line lines"} up text after a blank with spaces or Tabs</b>: it moves in the print.
      <div style={{ display: "flex", gap: ".8rem", alignItems: "center", flexWrap: "wrap", marginTop: ".8rem" }}>
        <span>Line up after the blank at</span>
        <input className={cls.inp} style={{ width: "8rem" }} type="number" min="0.5" step="0.5" value={tabCm} onChange={(e) => setTabCm(e.target.value)} aria-label="Place in cm" />
        <span>cm</span>
        <button className={cls.btn} disabled={!(Number(tabCm) >= 0.5)} onClick={() => lineUp(anchors)}>Line up</button>
      </div>
      <p className={cls.hint} style={{ margin: ".6rem 0 0" }}>From the left margin. Select two lines (e.g. name and occupation) to set both at once.</p>
    </div>
  );

  const card = page?.blocks || { buyerMode: "REPEAT", items: [] };
  const laterApplicants = blanks.filter((b) => b.setting === "DATA" && !b.removed && /^APPLICANT_[2-4]$/.test(b.applicantPick || ""));
  const blockButtons = (anchors) => canEdit && (
    <div style={{ marginTop: "1.2rem" }}>
      <p className={cls.lbl}>{anchors.length > 1 ? `These ${anchors.length} lines are` : "This line is"}</p>
      <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
        {["BUYER", "BUYER_COPY", "SIGNATURE", "SIGNATURE_COPY"].map((k) => (
          <button key={k} className={cls.btn} onClick={() => changeBlocks({ action: "SET", blockKey: k, anchors }, `Marked as ${BLOCK_NAMES[card.buyerMode][k]}`)}>
            {BLOCK_NAMES[card.buyerMode][k]}
          </button>
        ))}
      </div>
    </div>
  );

  if (!page) return <p className={cls.hint}>Opening the Word file as a page…</p>;

  return (
    <div>
      <div className={cls.mapBar}>
        <span className={`${cls.chip} ${cls.cErr}`} title="Spaces with nothing chosen yet">To fill · {count("todo")}</span>
        <span className={`${cls.chip} ${cls.cWarn}`} title="The system suggested a field: click the space to check it">Suggested, to check · {count("sug")}</span>
        <span className={`${cls.chip} ${cls.cOk}`}>Filled · {count("ok")}</span>
        <span className={`${cls.chip} ${cls.cGrey}`}>Hand-written · {count("hand")}</span>
        <span className={`${cls.chip} ${cls.cLt}`} title="Words you changed or removed on this page (Legal's Word file is not changed)">Word changes · {fixes.length}</span>
        {secs && <span className={`${cls.chip} ${cls.cLt}`} title="Lines where a new page starts: hover between two parts of the text, or click a line and use Page on the right">New pages · {secs.filter((s) => s.found && s.rule === "NEW_PAGE" && !s.wordBreakBefore).length + fixes.filter((e) => e.tool === "NEW_PAGE" && !e.recheck).length}</span>}
        <span style={{ flex: 1 }} />
        <span className={cls.savedNote} aria-live="polite">{saving > 0 ? "Saving…" : "All changes saved"}</span>
        <button className={cls.btn} onClick={() => setDrawer(true)}>Fields</button>
        <button className={`${cls.btn} ${cls.primary}`} onClick={() => nextBlank(toFill)}>Next space to fill →</button>
      </div>
      {laterApplicants.length > 0 && (
        <div className={cls.sugBox} style={{ marginBottom: "1.2rem" }}>
          {laterApplicants.length} blank(s) print the 2nd, 3rd or 4th applicant (blank {laterApplicants.map((b) => b.blankNo).join(", ")}): printed empty when a booking has fewer applicants.
        </div>
      )}
      {page.unplaced?.length > 0 && (
        <div className={cls.blockBox}>
          {page.unplaced.length} blank(s) could not be placed on the page (blank {page.unplaced.join(", ")}). Set them in the List view.
        </div>
      )}
      {(() => { const st = listState(); return st && !st.ok ? repairBox(st) : null; })()}
      {recheck.length > 0 && (
        <div className={cls.blockBox} ref={recheckRef}>
          <b>Needs re-check ({recheck.length}): the paragraph changed in the new file, so these page fixes are not applied.</b> Undo each one, then make it again if it is still needed.
          <ul className={cls.issues}>
            {recheck.map((e) => (
              <li key={e.editId}>{fixTitle(e)} · “{e.oldText}”{e.newText ? ` → “${e.newText}”` : ""}{" "}
                {canEdit && <button className={cls.link} onClick={() => undoFix(e.editId)}>Undo</button>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <BuyerCard card={card} canEdit={canEdit} onChange={changeBlocks} sheetRef={sheetRef} page={page} sheetKey={sheetKey}
        shown={shownApplicants} setShown={setShownApplicants} blanks={blanks} />

      <div className={`${cls.mapGrid} ${drawerOpen ? cls.mapGridOpen : ""}`}>
        <div className={cls.sheetWrap}>
          {/* the page HTML is built and escaped on the server from the Word file */}
          <div key={sheetKey} ref={sheetRef} className={`${cls.sheet} ${card.buyerMode === "KEEP_TYPED" ? cls.keepTyped : ""}`} onClick={onSheetClick} onMouseUp={onSheetMouseUp} dangerouslySetInnerHTML={html}
            style={{ fontSize: `${page.page?.fontSizePt || 11}pt`, maxWidth: `${page.page?.widthIn || 8.5}in` }} />
        </div>

        <div className={`${cls.fieldPanel} ${cls.drawer} ${drawerOpen ? cls.drawerOpen : ""}`} role="dialog" aria-label="Fill and change" aria-hidden={!drawerOpen}>
          <div className={cls.drawerFoot}>
            <span className={cls.hint} style={{ margin: 0 }}>{count("todo") + count("sug")} to fill</span>
            <button className={cls.btn} onClick={() => nextBlank(toFill, true)}>← Previous</button>
            <button className={`${cls.btn} ${cls.primary}`} onClick={() => nextBlank(toFill)}>Next space to fill →</button>
            <label className={cls.chk} style={{ flexBasis: "100%", fontSize: "1.2rem" }}>
              <input type="checkbox" checked={autoNext} onChange={(e) => changeAutoNext(e.target.checked)} /> After choosing, go to the next space
            </label>
          </div>
          <div className={cls.drawerHead}>
            <b>{!selected ? "Fields" : selected.kind === "buyer" ? "Buyer details" : selected.kind === "signs" ? "Signatures" : selected.kind === "blank" ? "What goes here?" : selected.kind === "words" ? "Change these words" : selected.kind === "fix" ? "A change you made" : "This line"}</b>
            <button type="button" className={cls.drawerX} aria-label="Close" title="Close (Esc)" onClick={closeDrawer}>×</button>
          </div>
          <div className={cls.selBox}>
            {selected?.kind === "buyer" && buyerBox()}
            {selected?.kind === "signs" && signsBox()}
            {!selected && (
              <>
                <p className={cls.selTitle} style={{ margin: 0 }}>Click a blank on the page.</p>
                <p className={cls.hint}>Then click a field below, or hold a field and drag it onto a blank.</p>
                {(may("EDIT_TEXT") || may("REMOVE_TEXT")) && <p className={cls.hint}>To change wording: select words, or click a paragraph.</p>}
              </>
            )}

            {selected?.kind === "words" && (
              <>
                <p className={cls.selTitle} style={{ margin: 0 }}>Selected words</p>
                <p className={cls.hint}>“{selected.text.length > 200 ? `${selected.text.slice(0, 200)}…` : selected.text}”</p>
                {may("REMOVE_TEXT") && may("ADD_FIELD_IN_SENTENCE") && (
                  <div style={{ marginBottom: "1.2rem" }}>
                    <button className={cls.btn} onClick={() => addFix({ tool: "ADD_BLANK", target: "WORDS", anchor: selected.anchor, start: selected.start, end: selected.end })}>
                      ＋ Make these words a blank
                    </button>
                    <p className={cls.hint} style={{ marginTop: ".4rem" }}>The typed words go and a new blank takes their place: then pick its field. Undo brings the words back.</p>
                  </div>
                )}
                {may("REMOVE_TEXT") && (
                  <button className={`${cls.btn} ${cls.danger}`} onClick={() => addFix({ tool: "REMOVE_TEXT", target: "WORDS", anchor: selected.anchor, start: selected.start, end: selected.end }, "Words removed")}>
                    Remove these words
                  </button>
                )}
                {may("EDIT_TEXT") && (
                  <div style={{ marginTop: "1.2rem" }}>
                    <label className={cls.lbl} htmlFor="newWords">Replace with</label>
                    <span style={{ display: "flex", gap: ".6rem" }}>
                      <input id="newWords" className={cls.optInp} value={newWords} onChange={(e) => setNewWords(e.target.value)} placeholder="New words" />
                      <button className={cls.btn} disabled={!newWords.trim()}
                        onClick={() => addFix({ tool: "EDIT_TEXT", target: "WORDS", anchor: selected.anchor, start: selected.start, end: selected.end, newText: newWords.trim() }, "Words edited")}>Use</button>
                    </span>
                  </div>
                )}
                {!may("EDIT_TEXT") && !may("REMOVE_TEXT") && <p className={cls.hint}>Changing wording is not allowed for this template.</p>}
              </>
            )}

            {selected?.kind === "para" && (
              <>
                <p className={cls.selTitle} style={{ margin: 0 }}>Paragraph</p>
                <p className={cls.hint}>“{selected.text.length > 160 ? `${selected.text.slice(0, 160)}…` : selected.text}”</p>
                {newPageBox(selected)}
                {may("REMOVE_TEXT") ? (
                  <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
                    <button className={`${cls.btn} ${cls.danger}`} onClick={() => addFix({ tool: "REMOVE_TEXT", target: "PARAGRAPH", anchor: selected.anchor }, "Paragraph removed")}>Remove paragraph</button>
                    {selected.inRow && (
                      <button className={`${cls.btn} ${cls.danger}`} onClick={() => addFix({ tool: "REMOVE_TEXT", target: "ROW", anchor: selected.anchor }, "Clause row removed")}>Remove the whole row</button>
                    )}
                  </div>
                ) : <p className={cls.hint}>Removing wording is not allowed for this template.</p>}
                {may("EDIT_TEXT") && <p className={cls.hint} style={{ marginTop: ".8rem" }}>To change words, select them on the page.</p>}
                {may("ADD_FIELD_IN_SENTENCE") && selected.at !== null && selected.at !== undefined && (
                  <div style={{ marginTop: "1.2rem" }}>
                    <button className={cls.btn} onClick={() => addFix({ tool: "ADD_BLANK", target: "POINT", anchor: selected.anchor, at: selected.at })}>
                      ＋ Add a blank here
                    </button>
                    <p className={cls.hint} style={{ marginTop: ".4rem" }}>{selected.empty ? "In this empty cell." : "Where you clicked (moved to the nearest gap between words). Click again here for a second blank."}</p>
                  </div>
                )}
                {lineUpBox(selected.align)}
                {canEdit && card.buyerMode === "REPEAT" && hasBuyerField(selected.anchor) && (
                  <div style={{ margin: ".4rem 0 1.2rem" }}>
                    <p className={cls.lbl}>Buyers</p>
                    <button className={`${cls.btn} ${cls.primary}`} onClick={() => setSelected({ kind: "buyer", near: selected.anchor })}>Use as Buyer details</button>
                    <p className={cls.hint} style={{ marginTop: ".4rem" }}>Finds this buyer's lines and shows what prints for each buyer before anything changes.</p>
                  </div>
                )}
                {canEdit && !selected.inRow && (() => {
                  const item = card.items.find((i) => i.blockKey === "PAYMENT_TABLE");
                  const here = item && item.anchors[0] === selected.anchor;
                  if (item && !here) return null;
                  return (
                    <div style={{ margin: ".4rem 0 1.2rem" }}>
                      <p className={cls.lbl}>Payment schedule</p>
                      <button className={cls.btn} onClick={() => setTableModal({ anchor: selected.anchor, item: here ? item : null })}>{here ? "Change the payment schedule table" : "＋ Make the payment schedule here"}</button>
                      {!here && <p className={cls.hint} style={{ marginTop: ".4rem" }}>When the Word file has no schedule table: the system makes one here from the booking's plan. You choose the columns.</p>}
                    </div>
                  );
                })()}
                {applicantListBox(selected)}
                {gapBox(selected)}
                {blockButtons([selected.anchor])}
              </>
            )}

            {selected?.kind === "paras" && (
              <>
                <p className={cls.selTitle} style={{ margin: 0 }}>{selected.anchors.length} paragraphs selected</p>
                <p className={cls.hint}>“{selected.text.length > 160 ? `${selected.text.slice(0, 160)}…` : selected.text}”</p>
                {lineUpBox(selected.align)}
                {blockButtons(selected.anchors)}
              </>
            )}

            {shownFix && (
              <>
                <p className={cls.selTitle} style={{ margin: 0 }}>{fixTitle(shownFix)}</p>
                <p className={cls.hint}>Was: “{shownFix.oldText}”{shownFix.newText ? <><br />Now: “{shownFix.newText}”</> : null}</p>
                <p className={cls.hint}>{shownFix.byName || "Legal"} · {fmtDate(shownFix.at)}{shownFix.removesBlanks?.length ? ` · blanks removed with it: ${shownFix.removesBlanks.join(", ")}` : ""}</p>
                {canEdit && <button className={cls.btn} onClick={() => undoFix(shownFix.editId)}>Undo</button>}
              </>
            )}

            {sel && (() => { const st = listState(); return st && !st.ok && st.sig.list.nameBlankNo === sel.blankNo ? repairBox(st, { marginBottom: "1.2rem" }) : null; })()}
            {sel && (
              <>
                <p className={cls.hint} style={{ margin: 0 }}>
                  Blank {sel.blankNo} · {BLANK_KINDS[sel.kind] || sel.kind} · in the Word file <span className={cls.mono}>{String(sel.rawText || "____").slice(0, 40)}</span>
                </p>
                {may("ADD_FIELD_IN_SENTENCE") && !sel.removed && sel.setting && (
                  <div style={{ marginTop: ".8rem" }}>
                    <button className={cls.btn} onClick={() => addFix({ tool: "ADD_BLANK", target: "POINT", afterBlank: sel.blankNo, spaceBefore: true })}>＋ Add a blank after this</button>
                    <p className={cls.hint} style={{ marginTop: ".4rem" }}>Right after it, with a space between (e.g. the full name after the salutation).</p>
                  </div>
                )}
                {canRemove && (
                  <div style={{ marginTop: ".8rem" }}>
                    <button className={`${cls.btn} ${cls.danger}`} onClick={removeSel} title="Or press Delete">Remove this blank</button>
                    <span className={cls.hint} style={{ marginLeft: ".8rem" }}>{sel.kind === "INSERTED" ? "Added by mistake? This takes it away." : "Its text is not printed; Undo brings it back."}</span>
                  </div>
                )}
                {sel.removed && (
                  <div className={cls.blockBox} style={{ marginTop: ".8rem" }}>
                    Removed{fixOfBlank && fixOfBlank.target !== "BLANK" ? " with the text around it" : ""}: it is not printed and needs no setting.
                    {canEdit && fixOfBlank && <div style={{ marginTop: ".6rem" }}><button className={cls.btn} onClick={() => undoFix(fixOfBlank.editId)}>Undo</button></div>}
                  </div>
                )}
                {!sel.removed && !sel.setting && chipState(sel) === "sug" && canEdit && (
                  <div className={cls.sugBox}>
                    Suggested: <b>{sel.suggestedFieldKey ? fieldsByKey[sel.suggestedFieldKey]?.label || sel.suggestedFieldKey : "Leave blank in the print"}</b>
                    {SUGGESTION_SOURCE[sel.suggestionSource] ? ` · found from ${SUGGESTION_SOURCE[sel.suggestionSource].toLowerCase()}` : ""}
                    <div style={{ marginTop: ".8rem" }}>
                      <button className={`${cls.btn} ${cls.primary}`}
                        onClick={() => (sel.suggestedFieldKey ? setField(sel.blankNo, sel.suggestedFieldKey) : save({ blankNo: sel.blankNo, setting: "LEAVE_BLANK", leaveAs: "LINE" }))}>
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
                {!sel.removed && (
                  <>
                    <p className={cls.selTitle} style={{ margin: "1.2rem 0 .6rem" }}>This blank prints as</p>
                    <div className={`${cls.opt} ${sel.setting === "DATA" ? cls.optOn : ""}`}>
                      <b>1 · Data from the system</b>
                      <span>{sel.setting === "DATA" ? `${fieldsByKey[sel.fieldKey]?.label || sel.fieldKey}${fieldsByKey[sel.fieldKey]?.sampleValue ? ` · e.g. ${fieldsByKey[sel.fieldKey].sampleValue}` : ""}` : "Click a field in the list below, or drag one onto the blank"}</span>
                      {sel.setting === "DATA" && sel.applicantPick && (
                        <span>
                          {pickName(sel.applicantPick)}
                          {sel.applicantPick !== "EACH" && canEdit && <> · <button className={cls.link} onClick={() => setPicking({ blankNo: sel.blankNo, fieldKey: sel.fieldKey, pick: sel.applicantPick })}>Change</button></>}
                        </span>
                      )}
                    </div>
                    <div className={`${cls.opt} ${sel.setting === "LEAVE_BLANK" ? cls.optOn : ""}`}>
                      <b>2 · Leave blank in the print</b>
                      <span>Filled by hand at signing. Show as{" "}
                        <select className={cls.optSel} disabled={!canEdit} aria-label="Blank style"
                          value={sel.setting === "LEAVE_BLANK" ? sel.leaveAs || "LINE" : ""}
                          onChange={(e) => save({ blankNo: sel.blankNo, setting: "LEAVE_BLANK", leaveAs: e.target.value })}>
                          {sel.setting !== "LEAVE_BLANK" && <option value="">choose</option>}
                          <option value="LINE">a line ______</option>
                          <option value="SPACE">an empty space</option>
                        </select>
                      </span>
                    </div>
                    <div className={`${cls.opt} ${sel.setting === "FIXED_TEXT" ? cls.optOn : ""}`}>
                      <b>3 · Fixed text</b>
                      <span style={{ display: "flex", gap: ".6rem" }}>
                        <input className={cls.optInp} value={fixedText} disabled={!canEdit} onChange={(e) => setFixedText(e.target.value)} placeholder="e.g. Pune" aria-label="Fixed text" />
                        <button className={cls.btn} disabled={!canEdit || !fixedText.trim()} onClick={() => save({ blankNo: sel.blankNo, setting: "FIXED_TEXT", fixedText: fixedText.trim() })}>Use</button>
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "1.6rem", flexWrap: "wrap" }}>
                      {canEdit && <button className={cls.link} onClick={() => onEdit(sel)}>Open the full setting pop-up</button>}
                      {sel.kind !== "INSERTED" && sel.kind !== "EMPTY_ROW" && canEdit && <button className={cls.link} onClick={() => addFix({ tool: "NOT_A_BLANK", target: "BLANK", blankNo: sel.blankNo })} title="The words are Legal's own text: printed as typed, without the highlight">Not a blank</button>}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <input className={cls.inp} style={{ margin: "1.2rem 0 .4rem" }} placeholder="Search fields" aria-label="Search fields" value={q} onChange={(e) => setQ(e.target.value)} />
          <div className={cls.palette}>
            {groups.map(([name, list]) => (
              <div key={name}>
                <p className={cls.fieldGroup}>{name}</p>
                {list.map((f) => (
                  <div key={f.key} className={`${cls.fieldItem} ${sel?.fieldKey === f.key ? cls.fieldOn : ""}`} onPointerDown={(e) => dragStart(e, f)}
                    role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" && selBlankNo) setField(selBlankNo, f.key); }}>
                    <b>⠿ {f.label}</b>
                    <span>{f.sampleValue ? `e.g. ${f.sampleValue}` : f.key}</span>
                  </div>
                ))}
              </div>
            ))}
            {!groups.length && <p className={cls.hint}>No field matches “{q}”.</p>}
          </div>
        </div>
      </div>
      {!drawerOpen && (count("todo") + count("sug")) > 0 && (
        <button type="button" className={cls.nextFab} onClick={() => nextBlank(toFill)} title="Go to the next space to fill">
          Next space to fill → <span>{count("todo") + count("sug")}</span>
        </button>
      )}
      {orderModal && (
        <FieldOrderModal fields={fields} initial={orderModal.initial}
          title={orderModal.kind === "buyer" ? "Which fields are in the buyer details?" : "Which fields are in each applicant's line?"}
          hint={orderModal.kind === "buyer" ? "Tick them in the order they stand in the buyer's lines: the spaces there are filled in that order, for every buyer."
            : "Tick them in the order they print, e.g. Salutation, Full name. Each applicant gets this line, numbered 1), 2) …"}
          spaces={orderModal.kind === "buyer" ? orderModal.spaces : null}
          onCancel={() => setOrderModal(null)}
          onSave={(keys) => {
            const m = orderModal;
            setOrderModal(null);
            if (m.kind === "buyer") applicantList(() => applyBuyerDetails(versionId, m.near || undefined, keys), "Buyer details set up with your fields");
            else applicantList(() => makeApplicantList(versionId, m.anchor, keys), "Applicant list made with your fields");
          }} />
      )}
      {tableModal && (
        <ScheduleTableModal fields={fields} item={tableModal.item} onCancel={() => setTableModal(null)}
          onSave={(v) => { const a = tableModal.anchor; setTableModal(null); changeBlocks({ action: "TABLE", anchor: a, ...v }, "Payment schedule table saved"); }}
          onRemove={() => { setTableModal(null); changeBlocks({ action: "TABLE", remove: true }, "Payment schedule table removed"); }} />
      )}
      {picking && (
        <ApplicantPickModal blankNo={picking.blankNo} field={fieldsByKey[picking.fieldKey]} pick={picking.pick}
          onCancel={() => setPicking(null)}
          onSave={(applicantPick) => { const p = picking; setPicking(null); save({ blankNo: p.blankNo, setting: "DATA", fieldKey: p.fieldKey, applicantPick }); }} />
      )}
    </div>
  );
}

export default MapFieldsPage;
